/**
 * PageHero — eDMS Design System (UX bolder-refresh, primitive #6)
 * The gradient page-header band: eyebrow + title + subtitle on the brand
 * gradient, with an actions slot and an optional content row beneath (e.g. a
 * strip of StatCards). Direction: UX_REFRESH_DIRECTION.md §1 "Lean into the brand".
 *
 * RTL-safe (logical properties). The decorative glow is static (no animation),
 * so it is inherently reduced-motion safe.
 */
import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import styles from './PageHero.module.css';

export interface PageHeroProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Small kicker above the title. */
  eyebrow?: ReactNode;
  /** Leading icon / logo / illustration. */
  icon?: ReactNode;
  /** Right-aligned action buttons. */
  actions?: ReactNode;
  /** 'gradient' (default): full brand gradient + white text. 'soft': tinted + dark text. */
  variant?: 'gradient' | 'soft';
  /** Heading element for the title. Default 'h1'. */
  titleAs?: 'h1' | 'h2';
  /** Extra content rendered below the header row (e.g. a StatCard strip). */
  children?: ReactNode;
  className?: string;
}

export function PageHero({
  title,
  subtitle,
  eyebrow,
  icon,
  actions,
  variant = 'gradient',
  titleAs = 'h1',
  children,
  className,
}: PageHeroProps) {
  const TitleTag = titleAs;
  return (
    <header className={clsx(styles.hero, variant === 'soft' && styles.soft, className)}>
      <div className={styles.glow} aria-hidden="true" />
      <div className={styles.row}>
        <div className={styles.content}>
          {icon && <span className={styles.icon} aria-hidden="true">{icon}</span>}
          <div className={styles.text}>
            {eyebrow && <span className={styles.eyebrow}>{eyebrow}</span>}
            <TitleTag className={styles.title}>{title}</TitleTag>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
        </div>
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
      {children && <div className={styles.extra}>{children}</div>}
    </header>
  );
}
